/**
 * Générateur — Automatisme : additionner et soustraire des décimaux
 * Attendu ID : 1044 | Niveau : 5ème | Domaine : Opérations
 * Rubrique : AUTOMATISME (programme 2025) — « 2,7 + 1,4 ; 3,4 − 0,8 ».
 * Labels (cartes exerciseur) :
 *   - Sommes et différences        (trous + QCM diagnostique + vérification)
 *   - Défi : deux décimales        (saisie, alignement, typé hors cœur)
 *
 * ── Plan de cartes validé (2026-08-31, cadrage prof) ───────────────────────
 * Cœur : deux décimaux à UN chiffre après la virgule, opérandes < 10, avec
 * FRANCHISSEMENT de l'unité (retenue / emprunt) majoritaire ; jamais deux
 * entiers purs ; jamais de résultat négatif ; pas de relatifs, pas de posé,
 * pas de 3 décimales, pas d'enchaînements. TOUT EN DIXIÈMES ENTIERS (le
 * défi en centièmes) : zéro flottant, chaque égalité re-parsée et
 * re-vérifiée (throw sinon).
 *
 * INVARIANTS :
 *   - couverture PAR SÉRIE de trous (4 lignes) : addition à retenue,
 *     soustraction à emprunt, résultat ENTIER (2,6 + 1,4 = 4 : tue « il y a
 *     toujours une virgule »), entier ± décimal (5 − 0,8 : l'emprunt sur un
 *     entier ; 3 + 0,9 : le sans-franchissement volontaire) ; ordre des
 *     opérandes varié en addition ;
 *   - QCM : items À FRANCHISSEMENT UNIQUEMENT (décision technique : sur un
 *     item sans retenue, « separateur » et « retenue_oubliee » donneraient
 *     LA BONNE réponse : distracteurs dégénérés) ; distracteurs mécanisés
 *     par opération, re-vérifiés ≠ bonne et distincts ;
 *   - PAIRE SACRIFIÉE aux fiches (l'exemple du programme) : 2,7 + 1,4 et
 *     ses soustractions liées (4,1 − 1,4 ; 4,1 − 2,7) ne sortent JAMAIS,
 *     F3 compris (invariant protégé par cdp1044.self.test.js) ;
 *   - F3 retrouver le nombre de départ par l'opération inverse : l'opérande
 *     est MASQUÉ (« … − 0,8 = 2,6 »), l'élève calcule vraiment (revue prof
 *     2026-08-31 : affiché, il recopiait la réponse) ; unicité triviale,
 *     pas de coup de pouce (l'aide dirait la réponse), explication
 *     post-réponse dédiée ;
 *   - explications contenu.explications par ligne/motif (« 7 dixièmes + 4
 *     dixièmes font 11 dixièmes : plus qu'une unité »).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeTexteTrou, tex,
} from '../../../utils';

const ATTENDU_ID = 1044;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_SOMMES = 'Sommes et différences';
const L_DEFI = 'Défi : deux décimales';

/** Rappel brouillon, même forme que les gens 6e (613, 615, 619) : une série
 *  de quatre calculs à franchissement, ça se pose (revue prof 2026-09-03). */
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Calcule de tête, ou pose au brouillon si tu en as besoin.</p>';

// ─── Le domaine (dixièmes entiers) ────────────────────────────────────────

/** La paire de l'intitulé, sacrifiée aux fiches (en dixièmes). */
export const PAIRE_SACRIFIEE = { a: 27, b: 14 };

/** La paire de la fiche du DÉFI, sacrifiée (en centièmes) : 3,45 + 1,2. */
export const PAIRE_SACRIFIEE_DEFI = { a100: 345, b100: 120 };

/** Un défi est sacrifié si la fiche en donne la réponse : 3,45 + 1,2 ou sa
 *  forme inverse 4,65 − 1,2 (4,65 − 3,45 est intirable : b100 doit être un
 *  multiple de 10 ; 3,45 − 1,2 reste autorisé, sa réponse n'y figure pas). */
const estSacrifieeDefi = (op, p0, p1) => {
    const { a100, b100 } = PAIRE_SACRIFIEE_DEFI;
    if (op === '+') return p0 === a100 && p1 === b100;
    return p0 === a100 + b100 && p1 === b100;
};

/** Une opération est sacrifiée si elle recompose l'exemple du programme :
 *  27 + 14 (dans les deux ordres), 41 − 14, 41 − 27. */
const estSacrifiee = (op, x10, y10) => {
    const { a, b } = PAIRE_SACRIFIEE;
    if (op === '+') return (x10 === a && y10 === b) || (x10 === b && y10 === a);
    return x10 === a + b && (y10 === a || y10 === b);
};

/** n dixièmes → écriture canonique française (« 4,1 », « 4 »). */
const fmtDix = (n10) => {
    const ent = Math.floor(n10 / 10);
    const d = n10 % 10;
    return d === 0 ? String(ent) : `${ent},${d}`;
};

/** n centièmes → écriture canonique (« 2,75 », « 4,1 », « 4 »). */
const fmtCent = (n100) => {
    const ent = Math.floor(n100 / 100);
    const c = String(n100 % 100).padStart(2, '0').replace(/0+$/, '');
    return c === '' ? String(ent) : `${ent},${c}`;
};

/** Écriture française → dixièmes entiers (re-parse de vérification). */
const parseDix = (s) => {
    const m = String(s).match(/^(\d+)(?:,(\d))?$/);
    if (!m) return null;
    return parseInt(m[1], 10) * 10 + (m[2] ? parseInt(m[2], 10) : 0);
};

/** AUTO-VÉRIFICATION d'une opération émise, par re-parse des écritures. */
const verifierOperation = (op, x10, y10, r10) => {
    const gauche = op === '+' ? x10 + y10 : x10 - y10;
    if (gauche !== r10 || r10 < 0 || x10 < 1 || y10 < 1 || x10 > 99 || y10 > 99
        || (op === '+' && r10 >= 100)
        || parseDix(fmtDix(x10)) !== x10 || parseDix(fmtDix(y10)) !== y10) {
        throw new Error(`gen_1044 : opération invalide ${x10} ${op} ${y10} = ${r10}`);
    }
    if (estSacrifiee(op, x10, y10)) throw new Error('gen_1044 : paire sacrifiée tirée');
    return { op, x10, y10, r10 };
};

const dix = (n10) => n10 % 10;

// ─── Tirages par catégorie (couverture prof) ──────────────────────────────

const tirerCategorie = (categorie) => {
    for (let essai = 0; essai < 80; essai++) {
        if (categorie === 'add_retenue') {
            // da + db ≥ 11 : le résultat garde une décimale (da + db = 10
            // donnerait un résultat entier : c'est la catégorie d'à côté).
            const da = randInt(2, 9);
            const db = randInt(Math.max(2, 11 - da), 9);
            if (da + db < 11) continue;
            const ea = randInt(0, 7);
            const eb = randInt(0, 7 - ea);
            const x = ea * 10 + da;
            const y = eb * 10 + db;
            if (x < 1 || y < 1 || estSacrifiee('+', x, y)) continue;
            return verifierOperation('+', x, y, x + y);
        }
        if (categorie === 'sub_emprunt') {
            // da ≥ 1 : le premier opérande n'est jamais un entier pur (5 − 2,8
            // relèverait de la catégorie « entier ± décimal »).
            const da = randInt(1, 8);
            const db = randInt(da + 1, 9);
            const ea = randInt(1, 9);
            const eb = randInt(0, ea - 1);
            const x = ea * 10 + da;
            const y = eb * 10 + db;
            if (y < 1 || x <= y || estSacrifiee('-', x, y)) continue;
            return verifierOperation('-', x, y, x - y);
        }
        if (categorie === 'resultat_entier') {
            if (randomChoice([true, false])) {
                const da = randInt(1, 9);
                const ea = randInt(0, 7);
                const eb = randInt(0, 7 - ea);
                const x = ea * 10 + da;
                const y = eb * 10 + (10 - da);
                if (x < 1 || y < 1 || estSacrifiee('+', x, y)) continue;
                return verifierOperation('+', x, y, x + y);
            }
            const d = randInt(1, 9);
            const ea = randInt(1, 9);
            const eb = randInt(0, ea - 1);
            const x = ea * 10 + d;
            const y = eb * 10 + d;
            if (y < 1 || x <= y || estSacrifiee('-', x, y)) continue;
            return verifierOperation('-', x, y, x - y);
        }
        // 'entier_et_decimal' : un opérande entier pur : 3 + 0,9 (sans
        // franchissement volontaire) ou 5 − 0,8 (l'emprunt sur un entier).
        if (randomChoice([true, false])) {
            const x = randInt(1, 8) * 10;
            const y = randInt(1, 9);
            if (estSacrifiee('+', x, y)) continue;
            return verifierOperation('+', x, y, x + y);
        }
        const x = randInt(1, 9) * 10;
        const y = randInt(1, 9);
        if (x <= y || estSacrifiee('-', x, y)) continue;
        return verifierOperation('-', x, y, x - y);
    }
    // Repli déterministe sûr par catégorie (jamais la paire sacrifiée).
    if (categorie === 'add_retenue') return verifierOperation('+', 38, 15, 53);
    if (categorie === 'sub_emprunt') return verifierOperation('-', 52, 17, 35);
    if (categorie === 'resultat_entier') return verifierOperation('+', 26, 14, 40);
    return verifierOperation('-', 50, 8, 42);
};

/** Verbalisation en dixièmes, spécifique à l'item (l'explication d'après). */
const expliquerItem = (it) => {
    const mot = it.op === '+' ? 'plus' : 'moins';
    return `${fmtDix(it.x10)} c'est ${it.x10} dixièmes, ${mot} ${it.y10} dixièmes : `
        + `${it.r10} dixièmes = ${fmtDix(it.r10)}.`;
};

const enTex = (s) => String(s).replace(',', '{,}');
const ligneTex = (it) => `${enTex(fmtDix(it.x10))} ${it.op === '+' ? '+' : '-'} ${enTex(fmtDix(it.y10))}`;

// ─── Carte 1, format A : la série à trous ─────────────────────────────────

const genSommesTrous = () => {
    for (let essai = 0; essai < 60; essai++) {
        const items = ['add_retenue', 'sub_emprunt', 'resultat_entier', 'entier_et_decimal'].map(tirerCategorie);
        const cles = new Set(items.map((it) => `${it.op}|${it.x10}|${it.y10}`));
        const resultats = new Set(items.map((it) => it.r10));
        if (cles.size < 4 || resultats.size < 4) continue;
        const melange = shuffle(items);
        const phrase = melange.map((it) => `$${ligneTex(it)} = {{${fmtDix(it.r10)}}}$`).join('\n');
        const payload = makeTexteTrou(
            phrase,
            `${RAPPEL_BROUILLON}<p><b>Calcule chaque somme ou différence.</b></p>`,
            { ...opts(L_SOMMES), mode: 'saisie' },
        );
        payload.contenu = { ...payload.contenu, explications: melange.map(expliquerItem) };
        payload._meta.params = { items: melange };
        return payload;
    }
    const items = [
        verifierOperation('+', 38, 15, 53),
        verifierOperation('-', 52, 17, 35),
        verifierOperation('+', 26, 14, 40),
        verifierOperation('-', 50, 8, 42),
    ];
    const payload = makeTexteTrou(
        items.map((it) => `$${ligneTex(it)} = {{${fmtDix(it.r10)}}}$`).join('\n'),
        `${RAPPEL_BROUILLON}<p><b>Calcule chaque somme ou différence.</b></p>`,
        { ...opts(L_SOMMES), mode: 'saisie' },
    );
    payload.contenu = { ...payload.contenu, explications: items.map(expliquerItem) };
    payload._meta.params = { items };
    return payload;
};

// ─── Carte 1, format B : QCM diagnostique (franchissement only) ───────────

/** Feedback DORMANT par motif : la version « après » des misconceptions. */
const EXPLICATIONS_QCM = {
    separateur: 'On ne calcule pas les deux parties séparément : 7 dixièmes + 4 dixièmes font 11 dixièmes, c\'est plus qu\'une unité.',
    retenue_oubliee: 'Les dixièmes dépassent 10 : une unité passe à gauche.',
    retenue_double: 'La retenue ne compte qu\'une fois.',
    soustraction_retournee: 'On ne retourne pas les dixièmes : quand il n\'y en a pas assez, on emprunte une unité.',
    emprunt_ignore: 'L\'unité empruntée se transforme en 10 dixièmes, elle ne disparaît pas.',
    emprunt_non_retranche: 'L\'unité empruntée doit être retirée de la partie entière.',
};

const distracteursPour = (it) => {
    const ea = Math.floor(it.x10 / 10);
    const eb = Math.floor(it.y10 / 10);
    const da = dix(it.x10);
    const db = dix(it.y10);
    if (it.op === '+') {
        return [
            // parties traitées comme deux entiers : « 3,11 »
            { texte: `${ea + eb},${da + db}`, code: 'separateur' },
            { texte: fmtDix(it.r10 - 10), code: 'retenue_oubliee' },
            { texte: fmtDix(it.r10 + 10), code: 'retenue_double' },
        ];
    }
    return [
        { texte: fmtDix((ea - eb) * 10 + Math.abs(da - db)), code: 'soustraction_retournee' },
        { texte: fmtDix((ea - eb - 1) * 10 + da), code: 'emprunt_ignore' },
        { texte: fmtDix(it.r10 + 10), code: 'emprunt_non_retranche' },
    ];
};

const genSommeQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const it = tirerCategorie(randomChoice(['add_retenue', 'sub_emprunt']));
        const faux = distracteursPour(it);
        // Aucun distracteur négatif possible par construction (emprunts et
        // retenues bornés) ; seule collision réelle : retournée = non
        // retranchée quand db = da + 5 → le Set la re-tire.
        const textes = [fmtDix(it.r10), ...faux.map((x) => x.texte)];
        if (new Set(textes).size !== 4) continue;
        const payload = makeQcm(
            textes,
            fmtDix(it.r10),
            `<p><b>Combien vaut ${tex(ligneTex(it))} ?</b></p>`,
            opts(L_SOMMES),
            false,
        );
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS_QCM };
        payload._meta.params = { ...it, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli déterministe.
    const it = verifierOperation('+', 38, 15, 53);
    const faux = distracteursPour(it);
    const payload = makeQcm(
        [fmtDix(it.r10), ...faux.map((x) => x.texte)],
        fmtDix(it.r10),
        `<p><b>Combien vaut ${tex(ligneTex(it))} ?</b></p>`,
        opts(L_SOMMES),
        false,
    );
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS_QCM };
    payload._meta.params = { ...it, distracteurs: faux.map(({ code }) => ({ code })) };
    return payload;
};

// ─── Carte 1, format C : la vérification par l'inverse ────────────────────

const genVerifInverse = () => {
    // « On a calculé : … − 0,8 = 2,6. Retrouve le nombre de départ :
    // 2,6 + 0,8 = ? » → l'élève CALCULE 3,4 : l'opérande de départ est
    // MASQUÉ (affiché, il serait la réponse écrite dans la question).
    // En addition, x ≠ y : recopier l'opérande visible ne doit pas donner
    // la réponse par coïncidence.
    // La paire sacrifiée est exclue à la source : ni sa forme masquée ni
    // sa forme inverse ne peuvent s'afficher.
    let it = tirerCategorie(randomChoice(['add_retenue', 'sub_emprunt']));
    for (let essai = 0; essai < 40 && it.op === '+' && it.x10 === it.y10; essai++) {
        it = tirerCategorie('add_retenue');
    }
    if (it.op === '+' && it.x10 === it.y10) it = verifierOperation('+', 38, 15, 53);
    const enonce = tex(`\\ldots ${it.op === '+' ? '+' : '-'} ${enTex(fmtDix(it.y10))} = ${enTex(fmtDix(it.r10))}`);
    const inverse = it.op === '-' ? '+' : '-';
    const question = tex(`${enTex(fmtDix(it.r10))} ${inverse} ${enTex(fmtDix(it.y10))}`);
    const reponse = fmtDix(it.x10);
    const payload = makeSaisie(
        reponse,
        `<p><b>On a calculé : ${enonce}.</b></p><p><b>Retrouve le nombre de départ : ${question} = ?</b></p>`,
        opts(L_SOMMES),
    );
    payload.contenu = {
        ...payload.contenu,
        explications: { verif_inverse: 'Refaire l\'opération dans l\'autre sens doit redonner le nombre de départ.' },
    };
    payload._meta.params = { ...it };
    return payload;
};

// ─── Carte 2 : le défi alignement (deux décimales) ────────────────────────

const genDefiAlignement = () => {
    for (let essai = 0; essai < 80; essai++) {
        // Un opérande à DEUX décimales (centième non nul), l'autre à une.
        const a100 = randInt(11, 899);
        if (a100 % 10 === 0) continue;
        // Exactement UNE décimale (un multiple de 100 serait un entier pur).
        const b100 = randInt(1, 89) * 10;
        if (b100 % 100 === 0) continue;
        const op = randomChoice(['+', '-']);
        const x = Math.max(a100, b100);
        const y = Math.min(a100, b100);
        const paire = op === '+' ? [a100, b100] : [x, y];
        const r = op === '+' ? paire[0] + paire[1] : paire[0] - paire[1];
        if (r <= 0 || r >= 1000) continue;
        if (estSacrifieeDefi(op, paire[0], paire[1])) continue;
        const f1 = fmtCent(paire[0]);
        const f2 = fmtCent(paire[1]);
        const payload = makeSaisie(
            fmtCent(r),
            `<p><b>Calcule : ${tex(`${enTex(f1)} ${op === '+' ? '+' : '-'} ${enTex(f2)}`)}</b></p>`,
            opts(L_DEFI),
        );
        payload.contenu = {
            ...payload.contenu,
            explications: { alignement: 'On aligne les virgules : les centièmes avec les centièmes, les dixièmes avec les dixièmes.' },
        };
        payload._meta.params = { op, a100: paire[0], b100: paire[1], r100: r };
        return payload;
    }
    const payload = makeSaisie(
        '4,15',
        `<p><b>Calcule : ${tex('2{,}75 + 1{,}4')}</b></p>`,
        opts(L_DEFI),
    );
    payload.contenu = {
        ...payload.contenu,
        explications: { alignement: 'On aligne les virgules : les centièmes avec les centièmes, les dixièmes avec les dixièmes.' },
    };
    payload._meta.params = { op: '+', a100: 275, b100: 140, r100: 415 };
    return payload;
};

// ─── Contrat distracteurs (référence de revue, format QCM) ────────────────

/**
 * Chaque erreur est une MISCONCEPTION NOMMÉE d'élève (cadrage prof
 * 2026-08-31), mécanisée par opération, re-vérifiée ≠ bonne.
 */
export const REGLES_DISTRACTEURS_QCM = {
    sommes_qcm: {
        support: 'une somme ou différence À FRANCHISSEMENT (sinon separateur et retenue_oubliee dégénèrent en bonne réponse)',
        assume: 'en addition, separateur est TOUJOURS la seule option à deux décimales (structurel : da + db ≥ 11) : un élève peut l\'éliminer à la forme, mais ce raisonnement de numération est déjà correct et le distracteur reste opérant pour qui a la misconception (arbitrage prof 2026-08-31, mesure 2597/2597)',
        fixes: {
            separateur: 'les deux parties traitées comme des entiers séparés (2,7 + 1,4 → 3,11) : LA misconception Prado',
            retenue_oubliee: 'dixièmes tronqués, l\'unité de retenue perdue (→ 3,1)',
            retenue_double: 'la retenue comptée deux fois (→ 5,1)',
            soustraction_retournee: 'les dixièmes retournés au lieu d\'emprunter (3,4 − 0,8 → 3,4)',
            emprunt_ignore: 'l\'unité retirée mais les dixièmes inchangés (→ 2,4)',
            emprunt_non_retranche: 'l\'emprunt fait aux dixièmes mais jamais retiré des unités (→ 3,6)',
        },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    sommes_texte_trou_saisie: genSommesTrous,
    sommes_qcm: genSommeQcm,
    verif_inverse_saisie: genVerifInverse,
    defi_alignement_saisie: genDefiAlignement,
};

export const CATEGORIES = [
    { id: 'sommes', label: L_SOMMES, formatsList: [
        { key: 'sommes_texte_trou_saisie', label: 'Saisie trous' },
        { key: 'sommes_qcm', label: 'QCM' },
        { key: 'verif_inverse_saisie', label: 'Saisie' },
    ] },
    { id: 'defi', label: L_DEFI, formatsList: [
        { key: 'defi_alignement_saisie', label: 'Saisie' },
    ] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
